/**
 * Funnel d'estimation immobilière.
 *
 * - Au clic sur [data-pdw-estim-trigger], pousse la valeur du champ adresse
 *   de la page (#pdw-estim-adresse) dans le champ hidden GF input_1 de la
 *   modal ciblée + préremplit les champs adresse structurés (rue, numéro,
 *   code postal, ville) en best-effort, puis Bootstrap ouvre la modal.
 * - Sur change d'un radio .pdw-estim-step, déclenche un clic sur le bouton
 *   "Suivant" GF (auto-advance) — sauf commerce / terrain sur l'étape type
 *   de bien : affichage du panneau dead-end « Votre cas est spécial ».
 * - Rend les champs number .pdw-estim-stepper en steppers −/+ (réinitialisés
 *   à chaque gform_post_render : GF AJAX remplace tout le markup à chaque
 *   changement de page).
 * - Met à jour la barre de progression (--pdw-estim-progress) à chaque
 *   re-render GF (event gform_post_render) en fonction de la page courante.
 */
(function () {
    'use strict';

    const TRIGGER_SELECTOR  = '[data-pdw-estim-trigger]';
    const ADRESSE_SELECTOR  = '#pdw-estim-adresse';
    const MODAL_SELECTOR    = '.pdw-estim-modal';
    const FORM_SELECTOR     = '[data-pdw-estim-form]';
    const HIDDEN_ADRESSE    = 'input[name="input_1"]';
    const STEP_RADIO        = '.pdw-estim-step input[type="radio"]';
    const NEXT_BUTTON       = '.gform_next_button';
    const PROGRESS_VAR      = '--pdw-estim-progress';
    const DEADEND_SELECTOR  = '[data-pdw-estim-deadend]';
    const DEADEND_BACK      = '[data-pdw-estim-deadend-back]';
    const DEADEND_VALUES    = ['commerce', 'terrain'];

    // Ids GF des champs adresse structurés (cf. import/update-gf5-estimation.php).
    const ADDR_FIELDS = { rue: 10, numero: 11, cp: 14, ville: 15 };

    /**
     * Parse best-effort d'une adresse libre « Rue du Parc 10, 4020 Liège »
     * en { rue, numero, cp, ville }. Tout ce qui ne matche pas reste vide
     * (l'utilisateur complète à l'étape 1 du funnel).
     */
    function parseAdresse(str) {
        const out = { rue: '', numero: '', cp: '', ville: '' };
        if (!str) return out;

        const parts = String(str).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        if (!parts.length) return out;

        // 1re partie : rue + numéro éventuel en fin (« Rue du Parc 10 »).
        const m = parts[0].match(/^(.*?)[\s]+(\d+\s?[a-zA-Z]?)$/);
        if (m) {
            out.rue    = m[1].trim();
            out.numero = m[2].trim();
        } else {
            out.rue = parts[0];
        }

        // Suite : code postal (4 chiffres) + ville (« 4020 Liège »).
        const rest = parts.slice(1).join(' ').trim();
        const m2   = rest.match(/(\d{4})\s*(.*)$/);
        if (m2) {
            out.cp    = m2[1];
            out.ville = (m2[2] || '').replace(/belgique$/i, '').trim();
        } else if (rest) {
            out.ville = rest;
        }

        return out;
    }

    /** Renseigne un input GF (sans écraser une saisie existante). */
    function fillGfInput(modal, formId, fieldId, value) {
        if (!value) return;
        const input = modal.querySelector('#input_' + formId + '_' + fieldId);
        if (!input || input.value) return;
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Trigger : pousse l'adresse de la page dans le hidden input du form GF
     * de la modal ciblée + préremplit les champs structurés de l'étape 1.
     */
    document.addEventListener('click', function (event) {
        const trigger = event.target.closest(TRIGGER_SELECTOR);
        if (!trigger) return;

        const targetId = trigger.getAttribute('data-bs-target');
        if (!targetId) return;

        const modal   = document.querySelector(targetId);
        if (!modal) return;

        const adresse = document.querySelector(ADRESSE_SELECTOR);
        if (!adresse) return;

        const hidden  = modal.querySelector(HIDDEN_ADRESSE);
        if (hidden) {
            hidden.value = adresse.value || '';
        }

        const gfForm = modal.querySelector('form[id^="gform_"]');
        if (gfForm && adresse.value) {
            const formId = gfForm.id.replace('gform_', '');
            const parsed = parseAdresse(adresse.value);
            fillGfInput(modal, formId, ADDR_FIELDS.rue, parsed.rue);
            fillGfInput(modal, formId, ADDR_FIELDS.numero, parsed.numero);
            fillGfInput(modal, formId, ADDR_FIELDS.cp, parsed.cp);
            fillGfInput(modal, formId, ADDR_FIELDS.ville, parsed.ville);
        }
    });

    /* ------------------------------------------------------------------
     * Dead-end commerce / terrain : « Votre cas est spécial ! »
     * ------------------------------------------------------------------ */

    function toggleDeadend(modal, show) {
        const body  = modal.querySelector('.pdw-estim-modal__body');
        const panel = modal.querySelector(DEADEND_SELECTOR);
        if (!body || !panel) return false;

        body.classList.toggle('is-deadend', show);
        panel.classList.toggle('d-none', !show);
        return true;
    }

    document.addEventListener('click', function (event) {
        const back = event.target.closest(DEADEND_BACK);
        if (!back) return;

        const modal = back.closest(MODAL_SELECTOR);
        if (!modal) return;

        toggleDeadend(modal, false);

        // Décoche le radio commerce / terrain pour permettre un nouveau choix.
        modal.querySelectorAll('.pdw-estim-step--bien input[type="radio"]:checked').forEach(function (radio) {
            radio.checked = false;
        });
    });

    /**
     * Auto-advance sur sélection d'un radio dans la modal :
     * un petit délai pour que GF enregistre la valeur, puis click "Suivant".
     * Exception : commerce / terrain → dead-end (pas d'estimation en ligne).
     */
    document.addEventListener('change', function (event) {
        const radio = event.target;
        if (!radio.matches || !radio.matches(STEP_RADIO)) return;

        const modal = radio.closest(MODAL_SELECTOR);
        if (!modal) return;

        if (radio.closest('.pdw-estim-step--bien') && DEADEND_VALUES.indexOf(radio.value) !== -1) {
            if (toggleDeadend(modal, true)) return;
            // Pas de panneau dans le DOM : on laisse le funnel continuer.
        }

        // On laisse GF traiter la valeur avant de cliquer.
        window.setTimeout(function () {
            const next = modal.querySelector(NEXT_BUTTON + ':not([style*="display: none"])');
            if (next) {
                next.click();
            }
        }, 120);
    });

    /* ------------------------------------------------------------------
     * Steppers −/+ sur les champs number .pdw-estim-stepper
     * ------------------------------------------------------------------ */

    function syncStepperState(input) {
        const wrap = input.closest('.pdw-stepper');
        if (!wrap) return;
        const minus = wrap.querySelector('[data-pdw-step="-1"]');
        if (minus) {
            minus.disabled = (parseInt(input.value, 10) || 0) <= 0;
        }
    }

    function makeStepperButton(step, label) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pdw-stepper__btn';
        btn.setAttribute('data-pdw-step', step);
        btn.setAttribute('aria-label', label);
        btn.textContent = step > 0 ? '+' : '−';
        return btn;
    }

    function initSteppers(root) {
        root.querySelectorAll('.pdw-estim-stepper .ginput_container_number').forEach(function (container) {
            if (container.classList.contains('pdw-stepper')) return;

            const input = container.querySelector('input');
            if (!input) return;

            container.classList.add('pdw-stepper');
            if (!input.value) {
                input.value = '0';
            }
            input.setAttribute('inputmode', 'numeric');
            container.insertBefore(makeStepperButton(-1, 'Diminuer'), input);
            container.appendChild(makeStepperButton(1, 'Augmenter'));
            syncStepperState(input);
        });
    }

    document.addEventListener('click', function (event) {
        const btn = event.target.closest('[data-pdw-step]');
        if (!btn) return;

        const input = btn.closest('.pdw-stepper').querySelector('input');
        if (!input) return;

        const step  = parseInt(btn.getAttribute('data-pdw-step'), 10) || 0;
        const value = Math.max(0, (parseInt(input.value, 10) || 0) + step);
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        syncStepperState(input);
    });

    document.addEventListener('change', function (event) {
        if (event.target.matches && event.target.matches('.pdw-stepper input')) {
            syncStepperState(event.target);
        }
    });

    /* ------------------------------------------------------------------
     * Progression + hooks de re-render GF
     * ------------------------------------------------------------------ */

    /**
     * Met à jour la progression à chaque (re-)render du form.
     * GF expose un event jQuery `gform_post_render` (formId, currentPage).
     */
    function updateProgress($, formId, currentPage) {
        const $form = $('#gform_' + formId);
        if (!$form.length) return;

        const modal = $form.closest(MODAL_SELECTOR).get(0);
        if (!modal) return;

        // On pose la variable sur la barre elle-même : son style inline initial
        // (--pdw-estim-progress: 0%) masquerait sinon toute valeur héritée du
        // modal pour le __fill. setProperty écrase cet inline.
        const bar = modal.querySelector('.pdw-estim-progress');
        if (!bar) return;

        // Total de pages = nombre de conteneurs .gform_page.
        const totalPages = $form.find('.gform_page').length || 1;
        // currentPage / totalPages → 1/9 = 11 %, … 9/9 = 100 %.
        const ratio = Math.min(1, Math.max(0, currentPage / totalPages));
        bar.style.setProperty(PROGRESS_VAR, (ratio * 100) + '%');
    }

    function onGfReady($) {
        $(document).on('gform_post_render', function (event, formId, currentPage) {
            updateProgress($, formId, currentPage);

            const form = document.getElementById('gform_' + formId);
            if (form && form.closest(MODAL_SELECTOR)) {
                initSteppers(form);
            }
        });

        // Premier render : `gform_post_render` est aussi déclenché au load
        // pour ajax forms, mais on force pour les forms non-ajax.
        $('.pdw-estim-modal form[id^="gform_"]').each(function () {
            const formId = $(this).attr('id').replace('gform_', '');
            updateProgress($, formId, 1);
            initSteppers(this);
        });
    }

    if (window.jQuery) {
        window.jQuery(onGfReady);
    } else {
        document.addEventListener('DOMContentLoaded', function () {
            if (window.jQuery) window.jQuery(onGfReady);
        });
    }
})();
